import MapView from '@arcgis/core/views/MapView';
import ArcGISLayer from '@arcgis/core/layers/Layer';
import { Layer } from '../../../models/layer-settings';
import { MapPlugin } from '../arcgis-types';
import StandardLayerConverter, { LayerConverter } from './utils/standard-layer-converter';
import PublicFSLBuilder, { FSLBuilder } from './utils/public-fsl-builder';
import { FULCRUM_PUBLIC_FSL_LAYER_PREFIX, isPublicFSL } from './public-feature-service-layers';

/**
 * The PreviewLayer plugin is intended to be used from within the Map Layers
 * (`/src/layer-uploader/index.tsx`) page.
 *
 * Because the Map Layer code specifically suppresses the map for GeoJson (or MBTile)
 * layers, this plugin only loads "Standard" and Public FSL layers. If previewing GeoJson is desired
 * in the future, this plugin can be updated to handle that case.
 *
 */
const PreviewLayer = (
  layer: Layer,
  apiToken: string,
  layerConverter?: LayerConverter,
  publicFSLBuilder?: FSLBuilder,
): MapPlugin => {
  const addLayerToMap = (arcLayer: ArcGISLayer | Promise<ArcGISLayer> | undefined, mapView: MapView) => {
    if (!arcLayer) {
      console.log("The layer couldn't be converted!");
      return;
    }

    mapView.map.add(arcLayer);

    // @ts-ignore
    arcLayer.when(() => {
      // @ts-ignore
      mapView.goTo(arcLayer.fullExtent);
    });
  };

  const loadPublicFsl = async (mapView: MapView) => {
    const builder = publicFSLBuilder || PublicFSLBuilder(FULCRUM_PUBLIC_FSL_LAYER_PREFIX);
    const arcLayer = await builder.build(layer);
    addLayerToMap(arcLayer, mapView);
  };

  const loadStandardLayer = (mapView: MapView) => {
    const converter = layerConverter || StandardLayerConverter;
    const arcLayer = converter(layer, apiToken);
    addLayerToMap(arcLayer, mapView);
  };

  const extendMap = (mapView: MapView) => {
    if (isPublicFSL(layer)) {
      loadPublicFsl(mapView);
    } else {
      loadStandardLayer(mapView);
    }
  };

  return {
    extendMap,
  };
};

export default PreviewLayer;
